import { useCallback, useEffect, useState } from "react";
import { Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { notifyBandejaChanged } from "../../lib/brand";

const KINDS = [["", "Todos"], ["movido", "Movido a cliente"], ["tipo", "Tipo corregido"]];

export const AiHistory = ({ reloadKey }) => {
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [kind, setKind] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/ai/jobs").then((r) => setJobs(r.data)).catch(() => {}); }, [reloadKey]);
  const load = useCallback(() => api.get("/ai/records", { params: { job_id: jobId || undefined, kind: kind || undefined } })
    .then((r) => setRows(r.data)).catch(() => setRows([])), [jobId, kind]);
  useEffect(() => { load(); }, [load, reloadKey]);
  const undo = async (fn) => {
    try {
      const r = (await fn()).data;
      toast.success(`${r.deshechos} ${r.deshechos === 1 ? "cambio deshecho" : "cambios deshechos"}${r.omitidos ? ` · ${r.omitidos} ya modificados por una persona` : ""}`);
      notifyBandejaChanged();
    } catch (err) { toast.error(errorMessage(err, "No se pudo deshacer")); }
    load();
  };
  return (
    <section className="tr-card space-y-3 p-4 sm:p-5" data-testid="ai-history-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-bold text-[#182235]">Historial de la IA</h2>
        <div className="flex flex-wrap gap-2">
          <select value={jobId} onChange={(e) => setJobId(e.target.value)} className="tr-input h-9 py-1 text-xs" data-testid="ai-history-job-select" aria-label="Lote">
            <option value="">Todos los lotes</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{`${formatDateTime(j.started_at)} · ${j.total} archivos${j.trial ? " (prueba)" : ""}`}</option>)}
          </select>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="tr-input h-9 py-1 text-xs" data-testid="ai-history-kind-select" aria-label="Tipo de cambio">
            {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {jobId && <button type="button" onClick={() => undo(() => api.post(`/ai/jobs/${jobId}/undo`, null, { timeout: 300000 }))} className="tr-btn-danger px-3 py-1.5 text-xs" data-testid="ai-history-undo-job">
            <Undo2 className="h-3.5 w-3.5" /> Deshacer todo este lote
          </button>}
        </div>
      </div>
      {rows.length === 0 ? <p className="text-sm text-[#5b667a]" data-testid="ai-history-empty">Sin cambios de la IA.</p> : (
        <ul className="max-h-[32rem] space-y-1.5 overflow-y-auto">
          {rows.map((r, i) => (
            <li key={r.id} className={`flex flex-wrap items-start justify-between gap-2 rounded-lg px-3 py-2 text-xs ${r.undone ? "bg-[#f2f4f7] opacity-60" : "bg-[#f5f8fd]"}`} data-testid={`ai-record-${i}`}>
              <div className="min-w-0">
                <div className="font-semibold text-[#182235]">{r.file_name} <span className="font-normal text-[#5b667a]">· {r.kind === "movido" ? "Movido a cliente" : "Tipo corregido"} · confianza {Math.round(r.confidence * 100)}%</span></div>
                <div className="text-[#5b667a]">{r.from_place} → <span className="font-semibold text-[#0f5ef7]">{r.to_place}</span></div>
                <div className="text-[#5b667a]">{r.reason_es} · {formatDateTime(r.at)}</div>
              </div>
              {r.undone ? <span className="text-[#5b667a]" data-testid={`ai-record-undone-${i}`}>Deshecho</span>
                : <button type="button" onClick={() => undo(() => api.post(`/ai/records/${r.id}/undo`))} className="tr-btn-light px-2.5 py-1.5 text-xs" data-testid={`ai-record-undo-${i}`}><Undo2 className="h-3.5 w-3.5" /> Deshacer</button>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
